import { DemoDialog } from 'app/src/features/DemoApp'
import { BuildBeneficiaries } from 'app/src/features/Shopping/shared/utils'
import type { shoppingApi } from 'app/src/services/welfareportal'
import { Image } from 'app/src/shared/components'
import { euroFormatterIt, formatDateDDMMYYYY, isDemoApp } from 'app/src/shared/helpers'
import { Button } from 'primereact/button'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export function VoucherWidget(props: { order: shoppingApi.OrderDetails }) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct' })
  const [showDemoDialog, setShowDemoDialog] = useState(false)

  const handleOpenUrl = (url: string) => {
    if (isDemoApp()) {
      setShowDemoDialog(true)
    } else {
      window.open(url, '_blank')
    }
  }

  const orderDetailVoucher = props.order?.purchaseDetails as shoppingApi.OrderDetailsPurchaseVoucher
  const amount = useMemo(() => {
    let importo = t('Common.freeAmount')

    if (props.order.priceDetails?.grossPrice && props.order.priceDetails.grossPrice > 0)
      importo = euroFormatterIt.format(props.order.priceDetails.grossPrice)
    return importo
  }, [t, props.order])

  const beneficiaries = useMemo(() => BuildBeneficiaries(props.order), [props.order])

  return (
    orderDetailVoucher && (
      <>
        <DemoDialog visible={showDemoDialog} onHide={() => setShowDemoDialog(false)} />
        <div className='printable card grid grid-cols-1 divide-x-0 divide-y-2 divide-dotted rounded-xl border border-solid border-[var(--primary-color)] bg-[var(--primary-color)] p-5 text-white lg:grid-cols-12 lg:divide-x-2 lg:divide-y-0'>
          <div className='lg:min-w[300px] col-span-12 p-2 text-center lg:col-span-4 lg:pr-5'>
            <div className='mb-4' dangerouslySetInnerHTML={{ __html: t('Common.pwsLogo') }} />
            {props.order.status === 'canceled' ? (
              <></>
            ) : (
              <>
                {orderDetailVoucher?.voucherImageUrl && (
                  <div className='mx-auto mb-4 max-h-fit max-w-fit rounded-xl border bg-white p-[2px]'>
                    <Image
                      pt={{ image: { className: 'max-w-[220px] max-h-[220px]' } }}
                      src={orderDetailVoucher?.voucherImageUrl}
                      alt='Immagine del codice voucher'
                      data-testid='hide-in-storybook'
                    />
                  </div>
                )}
                {orderDetailVoucher?.voucherCode && (
                  <>
                    <div
                      className='mb-1'
                      dangerouslySetInnerHTML={{
                        __html: t('Voucher.voucherCodeDescription'),
                      }}
                    />
                    <div className='text-base font-bold'>
                      <span>{orderDetailVoucher.voucherCode}</span>
                    </div>
                  </>
                )}
                {orderDetailVoucher?.voucherDetailsUrl && (
                  <div>
                    <Button
                      onClick={() => handleOpenUrl(orderDetailVoucher.voucherDetailsUrl!)}
                      rounded
                      pt={{ root: { className: 'rounded-3xl bg-[white] text-[var(--primary-color)] mt-4' } }}
                    >
                      {t('Voucher.getYourCodeButtonLabel')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
          <div className='lg:min-w[500px] col-span-12 mx-auto w-full p-2 lg:col-span-8 lg:pl-5'>
            <div className='mt-6 lg:mt-0'>{t('Voucher.orderIdLabel')}</div>
            <div className='mb-4 w-full break-words pt-2 font-bold uppercase lg:mb-0'>{props.order?.orderId ?? '-'}</div>
            <div className='mb-4 mt-2 hidden h-px w-full bg-white lg:block'></div>
            <div className='grid grid-cols-12'>
              <div className='col-span-12 mb-4 mt-2 lg:col-span-4 lg:my-0'>
                <div>{t('Voucher.productDescription')}</div>
                <div className='pt-2 font-bold uppercase'>{props.order.product?.productName}</div>
              </div>
              <div className='col-span-12 mt-2 lg:col-span-4 lg:col-start-7 lg:mt-0'>
                <div>{t('Voucher.priceDescription')}</div>
                <div className='pt-2 font-bold uppercase'>{amount}</div>
              </div>
            </div>
            <div className='mt-4 grid grid-cols-12'>
              <div className='col-span-12 mb-4 mt-2 lg:col-span-4 lg:my-0'>
                <div>{t('Voucher.buyerDescription')}</div>
                <div className='pt-2 font-bold uppercase'>{beneficiaries}</div>
              </div>
              <div className='col-span-12 mt-2 lg:col-span-4 lg:col-start-7 lg:mt-0'>
                <div>{t('Voucher.buyDateDescription')}</div>
                <div className='pt-2 font-bold uppercase'>
                  {orderDetailVoucher?.purchaseDate ? formatDateDDMMYYYY(new Date(orderDetailVoucher?.purchaseDate), true) : ''}
                </div>
              </div>
            </div>
            <div className='mt-4 grid grid-cols-12'>
              <div className='col-span-12 mt-2 lg:col-span-4 lg:mt-0'>
                <div>{t('Voucher.expiringDateDescription')}</div>
                <div className='pt-2 font-bold uppercase'>
                  {orderDetailVoucher?.expirationDate
                    ? formatDateDDMMYYYY(new Date(orderDetailVoucher?.expirationDate), true)
                    : t('Voucher.noExpiringDateDescription')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  )
}
